import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

// Detect the best default Antigravity API host
// - On localhost → use localhost
// - On a local network IP (192.168.x.x, 10.x.x.x, 172.x.x.x) → use that same IP
// - On a public domain (agent.mytaxniti.com) → use empty string (user must fill in their laptop IP)
function getDefaultApiHost() {
  const h = window.location.hostname;
  if (h === 'localhost' || h === '127.0.0.1') return h;
  if (/^192\.168\.|^10\.|^172\.(1[6-9]|2\d|3[01])\./.test(h)) return h;
  return ''; // public domain — user must enter their laptop's local IP
}

const defaultHost = getDefaultApiHost();

export default function AdminBridgeController() {
  const [httpUrl, setHttpUrl] = useState(defaultHost ? `http://${defaultHost}:5000` : '');
  const [wsUrl,   setWsUrl]   = useState(defaultHost ? `ws://${defaultHost}:9812`  : '');

  const [stats, setStats] = useState({ auto_run: 0, auto_allow: 0, remote_command: 0 });
  const [autoRunActive, setAutoRunActive] = useState(false);
  const [autoAllowActive, setAutoAllowActive] = useState(false);

  const [httpConnected, setHttpConnected] = useState(false);
  const [wsConnected, setWsConnected] = useState('DISCONNECTED');

  const [commandText, setCommandText] = useState('');
  const [switchChatTitle, setSwitchChatTitle] = useState('');
  const [logs, setLogs] = useState([]);
  const terminalEndRef = useRef(null);
  const wsRef = useRef(null);

  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showHelp, setShowHelp] = useState(!defaultHost); // auto-open help if on public domain
  const [laptopIp, setLaptopIp] = useState('');

  const showFeedback = (msg, isError = false) => {
    if (isError) { setErrorMsg(msg); setSuccessMsg(''); }
    else { setSuccessMsg(msg); setErrorMsg(''); }
    setTimeout(() => { setErrorMsg(''); setSuccessMsg(''); }, 4000);
  };

  const applyLaptopIp = () => {
    const ip = laptopIp.trim();
    if (!ip) return;
    setHttpUrl(`http://${ip}:5000`);
    setWsUrl(`ws://${ip}:9812`);
    setLaptopIp('');
    showFeedback(`Switched API target to ${ip}`);
  };

  const pollBridge = useCallback(async () => {
    if (!httpUrl) return;
    try {
      const statsRes = await axios.get(`${httpUrl}/stats`, { timeout: 3000 });
      if (statsRes.status === 200) setStats(statsRes.data);
      const stateRes = await axios.get(`${httpUrl}/get_command`, { timeout: 3000 });
      if (stateRes.status === 200) {
        setAutoRunActive(!!stateRes.data.auto_run);
        setAutoAllowActive(!!stateRes.data.auto_allow);
      }
      setHttpConnected(true);
    } catch {
      setHttpConnected(false);
    }
  }, [httpUrl]);

  useEffect(() => {
    pollBridge();
    const interval = setInterval(pollBridge, 3000);
    return () => clearInterval(interval);
  }, [pollBridge]);

  const connectWebSocket = useCallback(() => {
    if (!wsUrl) return;
    if (wsRef.current) wsRef.current.close();
    setWsConnected('CONNECTING');
    const ws = new WebSocket(wsUrl);
    ws.onopen = () => {
      setWsConnected('CONNECTED');
      setLogs(prev => [...prev, { ts: new Date().toLocaleTimeString(), type: 'system', text: `Connected to WebSocket at ${wsUrl}` }]);
    };
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setLogs(prev => [...prev, { ts: new Date().toLocaleTimeString(), type: 'chat', title: data.title || 'Session', content: data.content || '' }]);
      } catch {
        setLogs(prev => [...prev, { ts: new Date().toLocaleTimeString(), type: 'raw', text: event.data }]);
      }
    };
    ws.onclose = () => {
      setWsConnected('DISCONNECTED');
      setLogs(prev => [...prev, { ts: new Date().toLocaleTimeString(), type: 'system', text: 'WebSocket disconnected. Reconnecting in 5s...' }]);
      setTimeout(connectWebSocket, 5000);
    };
    ws.onerror = () => setWsConnected('DISCONNECTED');
    wsRef.current = ws;
  }, [wsUrl]);

  useEffect(() => {
    if (wsUrl) connectWebSocket();
    return () => { if (wsRef.current) wsRef.current.close(); };
  }, [connectWebSocket, wsUrl]);

  useEffect(() => {
    terminalEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const handleToggleAutoRun = async () => {
    try {
      const res = await axios.post(`${httpUrl}/toggle_auto_run`);
      if (res.status === 200) { setAutoRunActive(!!res.data.auto_run); showFeedback(`Auto-Run: ${res.data.auto_run ? 'ON' : 'OFF'}`); }
    } catch { showFeedback('Failed to toggle Auto-Run', true); }
  };

  const handleToggleAutoAllow = async () => {
    try {
      const res = await axios.post(`${httpUrl}/toggle_auto_allow`);
      if (res.status === 200) { setAutoAllowActive(!!res.data.auto_allow); showFeedback(`Auto-Allow: ${res.data.auto_allow ? 'ON' : 'OFF'}`); }
    } catch { showFeedback('Failed to toggle Auto-Allow', true); }
  };

  const handleSendCommand = async (e) => {
    e?.preventDefault();
    if (!commandText.trim()) return;
    try {
      const res = await axios.post(`${httpUrl}/send_command`, { text: commandText.trim() });
      if (res.status === 200) { showFeedback(`Command queued (Position: ${res.data.position || 1})`); setCommandText(''); }
    } catch (err) {
      showFeedback(err.response?.status === 403 ? 'Freemium limit reached. Upgrade to unlock.' : 'Failed to send command. Is VS Code bridge running?', true);
    }
  };

  const handleStartNewChat = async () => {
    try {
      await axios.post(`${httpUrl}/start-new-chat`);
      showFeedback('Signal sent: Start new chat');
    } catch { showFeedback('Failed. Is bridge running?', true); }
  };

  const handleSwitchChat = async (e) => {
    e?.preventDefault();
    if (!switchChatTitle.trim()) return;
    try {
      await axios.post(`${httpUrl}/switch_chat`, { title: switchChatTitle.trim() });
      showFeedback(`Switched to: "${switchChatTitle.trim()}"`);
      setSwitchChatTitle('');
    } catch { showFeedback('Failed to switch chat', true); }
  };

  const isPublicDomain = !defaultHost;
  const needsIpSetup = isPublicDomain && !httpUrl;

  return (
    <div className="bc-wrap">
      <style>{`
        *, *::before, *::after { box-sizing: border-box; }
        .bc-wrap {
          background: #090d16;
          min-height: 100vh;
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
          color: #e2e8f0;
          padding: 1rem;
        }
        @media (min-width: 640px) { .bc-wrap { padding: 2rem clamp(1rem,5vw,3rem); } }

        /* Header */
        .bc-header {
          display: flex;
          flex-wrap: wrap;
          align-items: flex-start;
          justify-content: space-between;
          gap: 0.75rem;
          border-bottom: 1px solid #1e293b;
          padding-bottom: 1.25rem;
          margin-bottom: 1.5rem;
        }
        .bc-header h1 { font-size: clamp(1.2rem,4vw,1.75rem); font-weight: 700; margin: 0; color: #f8fafc; }
        .bc-header p  { font-size: 0.82rem; color: #64748b; margin: 0.2rem 0 0; }
        .bc-badges { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; }

        /* Help Banner */
        .bc-help {
          background: linear-gradient(135deg,#0f1e3d,#0c1829);
          border: 1px solid #1e3a5f;
          border-radius: 12px;
          padding: 1rem 1.25rem;
          margin-bottom: 1.25rem;
        }
        .bc-help-header { display:flex; justify-content:space-between; align-items:center; cursor:pointer; user-select:none; }
        .bc-help-header span { font-weight:700; font-size:0.95rem; color:#60a5fa; }
        .bc-help-header button { background:none; border:none; color:#60a5fa; cursor:pointer; font-size:1rem; padding:0; }
        .bc-help-body { margin-top:0.85rem; }
        .bc-help-body ol { margin:0.5rem 0 0; padding-left:1.25rem; }
        .bc-help-body li { font-size:0.85rem; color:#cbd5e1; margin-bottom:0.4rem; line-height:1.5; }
        .bc-help-body li strong { color:#93c5fd; }
        .bc-ip-row { display:flex; gap:0.6rem; margin-top:0.85rem; }
        .bc-ip-row input {
          flex:1; background:#1e293b; border:1px solid #334155; border-radius:8px;
          color:#f1f5f9; padding:0.6rem 0.8rem; font-size:0.88rem; outline:none; min-width:0;
        }
        .bc-ip-row input:focus { border-color:#3b82f6; }

        /* Grid — stacks on mobile, 2-col on md+ */
        .bc-grid {
          display:grid;
          grid-template-columns:1fr;
          gap:1.25rem;
        }
        @media (min-width: 768px) {
          .bc-grid { grid-template-columns: minmax(0,2fr) minmax(0,3fr); }
          .bc-col-right { display:flex; flex-direction:column; gap:1.25rem; }
        }
        .bc-col-left { display:flex; flex-direction:column; gap:1.25rem; }

        /* Cards */
        .bc-card {
          background: #111827;
          border: 1px solid #1f2937;
          border-radius: 12px;
          padding: 1.25rem;
        }
        .bc-card h2 { font-size:1rem; font-weight:600; margin:0 0 1rem; color:#f1f5f9; display:flex; align-items:center; gap:0.4rem; }

        /* Inputs */
        .bc-field { display:flex; flex-direction:column; gap:0.3rem; margin-bottom:0.85rem; }
        .bc-field label { font-size:0.7rem; font-weight:700; color:#94a3b8; text-transform:uppercase; letter-spacing:0.05em; }
        .bc-field input, .bc-field textarea {
          background:#1f2937; border:1px solid #374151; border-radius:8px;
          color:#f1f5f9; padding:0.65rem 0.85rem; font-size:0.88rem; outline:none;
          font-family:inherit; transition:border-color 0.2s; width:100%;
        }
        .bc-field input:focus, .bc-field textarea:focus { border-color:#3b82f6; }
        .bc-field textarea { resize:vertical; min-height:80px; }

        /* Buttons */
        .btn-p {
          background:#2563eb; color:#fff; border:none; border-radius:8px;
          padding:0.75rem 1.2rem; font-size:0.9rem; font-weight:600; cursor:pointer;
          transition:all 0.2s; font-family:inherit; min-height:44px;
        }
        .btn-p:hover { background:#1d4ed8; }
        .btn-p:disabled { opacity:0.45; cursor:not-allowed; }
        .btn-s {
          background:#1f2937; color:#e2e8f0; border:1px solid #374151; border-radius:8px;
          padding:0.75rem 1.2rem; font-size:0.9rem; font-weight:600; cursor:pointer;
          transition:all 0.2s; font-family:inherit; min-height:44px;
        }
        .btn-s:hover { background:#374151; }
        .btn-s:disabled { opacity:0.45; cursor:not-allowed; }
        .btn-full { width:100%; }
        .btn-row { display:flex; gap:0.6rem; flex-wrap:wrap; }
        .btn-row .btn-p, .btn-row .btn-s { flex:1; min-width:0; }

        /* Status badge */
        .badge {
          display:inline-flex; align-items:center; gap:0.3rem;
          font-size:0.7rem; font-weight:700; padding:0.3rem 0.65rem;
          border-radius:99px; text-transform:uppercase; white-space:nowrap;
        }
        .badge::before { content:''; width:7px; height:7px; border-radius:50%; display:inline-block; }
        .badge.connected  { background:rgba(16,185,129,.12); color:#10b981; border:1px solid rgba(16,185,129,.25); }
        .badge.connected::before  { background:#10b981; box-shadow:0 0 6px #10b981; }
        .badge.disconnected { background:rgba(239,68,68,.1); color:#ef4444; border:1px solid rgba(239,68,68,.2); }
        .badge.disconnected::before { background:#ef4444; }
        .badge.connecting { background:rgba(245,158,11,.1); color:#f59e0b; border:1px solid rgba(245,158,11,.2); }
        .badge.connecting::before { background:#f59e0b; animation:blink 1s infinite; }
        @keyframes blink { 50% { opacity:0.2; } }

        /* Toggle switch */
        .toggle-row { display:flex; align-items:center; justify-content:space-between; padding:0.75rem 0; border-bottom:1px solid #1f2937; }
        .toggle-row:last-child { border-bottom:none; }
        .toggle-lbl span:first-child { font-weight:600; font-size:0.9rem; color:#f1f5f9; display:block; }
        .toggle-lbl span:last-child { font-size:0.75rem; color:#64748b; }
        .switch { position:relative; display:inline-block; width:46px; height:26px; flex-shrink:0; }
        .switch input { opacity:0; width:0; height:0; }
        .slider { position:absolute; cursor:pointer; top:0; left:0; right:0; bottom:0; background:#374151; transition:.3s; border-radius:26px; }
        .slider::before { position:absolute; content:""; height:18px; width:18px; left:4px; bottom:4px; background:#fff; transition:.3s; border-radius:50%; }
        input:checked+.slider { background:#2563eb; }
        input:checked+.slider::before { transform:translateX(20px); }

        /* Stats */
        .stats-row { display:grid; grid-template-columns:repeat(3,1fr); gap:0.75rem; }
        .stat-box { background:#1f2937; border:1px solid #374151; border-radius:8px; padding:0.85rem 0.5rem; text-align:center; }
        .stat-val { font-size:1.5rem; font-weight:700; color:#3b82f6; }
        .stat-lbl { font-size:0.7rem; color:#94a3b8; text-transform:uppercase; font-weight:600; letter-spacing:0.05em; }

        /* Terminal */
        .terminal {
          font-family:'Courier New',monospace; background:#05070f; border:1px solid #1f2937;
          border-radius:8px; height:320px; overflow-y:auto; padding:0.85rem;
          display:flex; flex-direction:column; gap:0.65rem; font-size:0.8rem;
        }
        @media (min-width: 640px) { .terminal { height: 380px; } }
        .log-row { line-height:1.4; word-break:break-word; }
        .log-time { color:#475569; margin-right:0.4rem; }
        .log-sys { color:#f59e0b; }
        .log-title { color:#3b82f6; font-weight:700; }
        .log-content { color:#a7f3d0; white-space:pre-wrap; padding-left:1rem; border-left:1px solid #1e293b; margin-top:0.2rem; }
        .log-raw { color:#e2e8f0; }
        .terminal-idle { color:#475569; text-align:center; padding-top:6rem; }

        /* Feedback toast */
        .toast {
          position:fixed; bottom:20px; right:16px; left:16px;
          padding:0.85rem 1.25rem; border-radius:10px; font-size:0.88rem; z-index:999;
          animation:slideUp 0.25s cubic-bezier(.16,1,.3,1); box-shadow:0 8px 30px rgba(0,0,0,.5);
          font-weight:500; display:flex; align-items:center; gap:0.5rem;
        }
        @media (min-width: 480px) { .toast { left:auto; width:auto; max-width:380px; } }
        .toast.ok { background:#064e3b; border:1px solid #059669; color:#34d399; }
        .toast.err { background:#7f1d1d; border:1px solid #dc2626; color:#fca5a5; }
        @keyframes slideUp { from{transform:translateY(12px);opacity:0} to{transform:translateY(0);opacity:1} }

        /* Needs-IP warning card */
        .needs-ip-card {
          background: linear-gradient(135deg,#1c0b0b,#1a0a00);
          border: 1px solid #7c2d12;
          border-radius: 12px;
          padding: 1.25rem;
          text-align: center;
        }
        .needs-ip-card p { color:#fca5a5; font-size:0.9rem; margin:0 0 0.75rem; }
      `}</style>

      {/* Toasts */}
      {successMsg && <div className="toast ok">✓ {successMsg}</div>}
      {errorMsg   && <div className="toast err">⚠ {errorMsg}</div>}

      {/* Header */}
      <div className="bc-header">
        <div>
          <h1>🎛️ Antigravity Bridge Controller</h1>
          <p>Remote control for your Antigravity IDE — Auto-Run, Auto-Allow, command injection & live feed.</p>
        </div>
        <div className="bc-badges">
          <span className={`badge ${httpConnected ? 'connected' : 'disconnected'}`}>
            REST {httpConnected ? 'Online' : 'Offline'}
          </span>
          <span className={`badge ${wsConnected.toLowerCase()}`}>
            WS {wsConnected}
          </span>
        </div>
      </div>

      {/* Help / How to Use Banner */}
      <div className="bc-help">
        <div className="bc-help-header" onClick={() => setShowHelp(v => !v)}>
          <span>ℹ️ How to Connect from Mobile / Remote Device</span>
          <button>{showHelp ? '▲ Hide' : '▼ Show'}</button>
        </div>
        {showHelp && (
          <div className="bc-help-body">
            <ol>
              <li>Make sure <strong>Antigravity IDE</strong> is open on your laptop with the extension running.</li>
              <li>
                <strong>Same WiFi as laptop?</strong> — The URL fields below are already set to the correct IP.
                Just tap <em>"Apply & Reconnect"</em> and you're done.
              </li>
              <li>
                <strong>Using 4G / different network?</strong> — Find your laptop's local IP by running{' '}
                <code style={{background:'#1e293b',padding:'0.1em 0.4em',borderRadius:4,fontSize:'0.82rem'}}>ipconfig</code> on the laptop (look for Wi-Fi IPv4).
                Enter it below and tap Connect.
              </li>
              <li>
                <strong>Laptop IP not reachable on 4G?</strong> — You need router port-forwarding for ports{' '}
                <strong>5000</strong> (REST) and <strong>9812</strong> (WebSocket) pointing to your laptop, OR use a VPN.
              </li>
            </ol>
            {isPublicDomain && (
              <div className="bc-ip-row">
                <input
                  type="text"
                  placeholder="Enter your laptop's local IP (e.g. 192.168.1.5)"
                  value={laptopIp}
                  onChange={e => setLaptopIp(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && applyLaptopIp()}
                />
                <button className="btn-p" onClick={applyLaptopIp} style={{whiteSpace:'nowrap'}}>Connect</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* If no URL set yet, show prominent setup card */}
      {needsIpSetup ? (
        <div className="needs-ip-card">
          <p>⚠️ Connection not configured. Enter your laptop's local IP in the help panel above to get started.</p>
          <button className="btn-p" onClick={() => setShowHelp(true)}>Open Setup Guide</button>
        </div>
      ) : (
        <div className="bc-grid">

          {/* LEFT COLUMN */}
          <div className="bc-col-left">

            {/* Endpoint Config */}
            <div className="bc-card">
              <h2>⚙️ Endpoint Configuration</h2>
              <div className="bc-field">
                <label>REST API URL (port 5000)</label>
                <input type="text" value={httpUrl} onChange={e => setHttpUrl(e.target.value)} placeholder="http://192.168.1.5:5000" />
              </div>
              <div className="bc-field">
                <label>WebSocket URL (port 9812)</label>
                <input type="text" value={wsUrl} onChange={e => setWsUrl(e.target.value)} placeholder="ws://192.168.1.5:9812" />
              </div>
              <button className="btn-p btn-full" onClick={() => { pollBridge(); connectWebSocket(); }}>
                Apply & Reconnect
              </button>
            </div>

            {/* Automation */}
            <div className="bc-card">
              <h2>🤖 Click Automation</h2>
              <div className="toggle-row">
                <div className="toggle-lbl">
                  <span>Auto-Run Actions</span>
                  <span>Automatically press "Run" prompts</span>
                </div>
                <label className="switch">
                  <input type="checkbox" checked={autoRunActive} onChange={handleToggleAutoRun} disabled={!httpConnected} />
                  <span className="slider" />
                </label>
              </div>
              <div className="toggle-row">
                <div className="toggle-lbl">
                  <span>Auto-Allow Permissions</span>
                  <span>Auto-approve sandboxed accesses</span>
                </div>
                <label className="switch">
                  <input type="checkbox" checked={autoAllowActive} onChange={handleToggleAutoAllow} disabled={!httpConnected} />
                  <span className="slider" />
                </label>
              </div>
            </div>

            {/* Stats */}
            <div className="bc-card">
              <h2>📊 Session Stats</h2>
              <div className="stats-row">
                <div className="stat-box">
                  <div className="stat-val">{stats.auto_run || 0}</div>
                  <div className="stat-lbl">Auto-Runs</div>
                </div>
                <div className="stat-box">
                  <div className="stat-val">{stats.auto_allow || 0}</div>
                  <div className="stat-lbl">Allows</div>
                </div>
                <div className="stat-box">
                  <div className="stat-val">{stats.remote_command || 0}</div>
                  <div className="stat-lbl">Cmds</div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="bc-col-right">

            {/* Remote Control */}
            <div className="bc-card">
              <h2>🕹️ Remote Control Console</h2>

              <form onSubmit={handleSendCommand} style={{ marginBottom: '1.25rem' }}>
                <div className="bc-field">
                  <label>Remote Prompt / Command</label>
                  <textarea
                    value={commandText}
                    onChange={e => setCommandText(e.target.value)}
                    placeholder="e.g. Add a validation constraint to the ItrApplication model and write a JUnit test..."
                    rows={3}
                  />
                </div>
                <div className="btn-row">
                  <button type="submit" className="btn-p" disabled={!commandText.trim() || !httpConnected}>
                    ▶ Queue Prompt
                  </button>
                  <button type="button" className="btn-s" onClick={handleStartNewChat} disabled={!httpConnected}>
                    🆕 New Chat
                  </button>
                </div>
              </form>

              <form onSubmit={handleSwitchChat}>
                <div className="bc-field">
                  <label>Switch Conversation (by Title)</label>
                  <div style={{ display:'flex', gap:'0.6rem' }}>
                    <input
                      type="text"
                      value={switchChatTitle}
                      onChange={e => setSwitchChatTitle(e.target.value)}
                      placeholder="e.g. Refactoring Auth Module"
                      style={{ flex:1, minWidth:0 }}
                    />
                    <button type="submit" className="btn-s" disabled={!switchChatTitle.trim() || !httpConnected} style={{ flexShrink:0 }}>
                      Switch
                    </button>
                  </div>
                </div>
              </form>
            </div>

            {/* Live Feed */}
            <div className="bc-card" style={{ flex:1, display:'flex', flexDirection:'column' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1rem', flexWrap:'wrap', gap:'0.5rem' }}>
                <h2 style={{ margin:0 }}>📺 Live Chat Feed</h2>
                <button className="btn-s" onClick={() => setLogs([])} style={{ padding:'0.4rem 0.85rem', fontSize:'0.78rem' }}>
                  Clear
                </button>
              </div>
              <div className="terminal">
                {logs.length === 0 ? (
                  <div className="terminal-idle">&gt; Idle. Start typing in Antigravity IDE to stream messages here...</div>
                ) : (
                  logs.map((log, i) => (
                    <div key={i} className="log-row">
                      <span className="log-time">[{log.ts}]</span>
                      {log.type === 'system' && <span className="log-sys">&gt;&gt;&gt; {log.text}</span>}
                      {log.type === 'chat' && (
                        <>
                          <span className="log-title">&lt;{log.title}&gt;</span>
                          <div className="log-content">{log.content}</div>
                        </>
                      )}
                      {log.type === 'raw' && <span className="log-raw">{log.text}</span>}
                    </div>
                  ))
                )}
                <div ref={terminalEndRef} />
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
